import { daysRange, formatIsoDate, isSameDate } from '@src/shared/lib';
import { format } from 'date-fns';
import type {
  Absence,
  AbsenceChangeOrigin,
  AbsenceChangeOriginDto,
  AbsenceDto,
  AbsenceType,
  AbsenceTypeDto,
  AbsenceWithdrawalRequest,
  AbsenceWithdrawalRequestDto,
  ActivityTypeGroup,
  ActivityTypeGroupDto,
  SelectedAbsence,
  TimelineSlot,
} from '../types';
import { HR_APPROVAL, MANAGER_APPROVAL } from '../types';
import { userResourceId } from './resources';

export function selectAbsences(data: Array<AbsenceDto>) {
  return data.map(absenceFromDto);
}

function absenceFromDto(absenceDto: AbsenceDto): Absence {
  return {
    id: absenceDto.id,
    userId: absenceDto.acti_user,
    typeId: absenceDto.acti_type,
    colourFg: absenceDto.acti_colour_fg,
    colourBg: absenceDto.acti_colour_bg,
    date: new Date(absenceDto.acti_date),
    dateStr: absenceDto.acti_date,
    startTime: toAbsenceTime(absenceDto.acti_start_time),
    endTime: toAbsenceTime(absenceDto.acti_end_time),
    absenceTentative: absenceDto.acti_tentative,
    allDay: absenceDto.all_day,
    approvedManager: absenceDto.acab_approved_manager,
    approvedHr: absenceDto.acab_approved_hr,
    notes: absenceDto.acab_notes,
    notesHr: absenceDto.acab_notes_hr,
    export: absenceDto.acab_export,
    workdays: absenceDto.workday_count,
    withdrawalPending: absenceDto.withdrawal_pending,
    withdrawalRequested: absenceDto.withdrawal_requested,
    changePending: absenceDto.change_pending,
    changedFrom: toChangeOrigin(absenceDto.changed_from),
    withdrawalRequest: toWithdrawalRequest(absenceDto.withdrawal_request),
  };
}

/**
 * Every distinct withdrawal request the given days are covered by.
 *
 * Usually one, but a selection is free to span more than a single request and
 * routinely does: deciding part of a run leaves the rest asking on its own, and
 * granting part of one hands those days to HR while the remainder stays with
 * the manager. Taking only the first would describe one request beside buttons
 * that answer another.
 *
 * Carried on every day it covers, so the same request arrives once per day and
 * is folded back into one by what identifies it -- who is being waited on, over
 * which run, since when.
 */
export function withdrawalRequestsIn(
  absences: Array<Pick<Absence, 'withdrawalRequest'>>,
): AbsenceWithdrawalRequest[] {
  const found = new Map<string, AbsenceWithdrawalRequest>();
  for (const { withdrawalRequest } of absences) {
    if (!withdrawalRequest) continue;
    const { approverId, dateFrom, dateTo, requestedAt } = withdrawalRequest;
    found.set(
      `${approverId}|${dateFrom}|${dateTo}|${requestedAt}`,
      withdrawalRequest,
    );
  }
  return [...found.values()].sort((one, other) =>
    one.dateFrom.localeCompare(other.dateFrom),
  );
}

function toWithdrawalRequest(
  request: AbsenceWithdrawalRequestDto | null | undefined,
): AbsenceWithdrawalRequest | null {
  if (!request) return null;
  return {
    dateFrom: request.date_from,
    dateTo: request.date_to,
    approverId: request.approver,
    approverName: request.approver_name,
    approverRole: request.approver_role,
    requestedAt: request.requested_at,
  };
}

function toChangeOrigin(
  origin: AbsenceChangeOriginDto | null | undefined,
): AbsenceChangeOrigin | null {
  if (!origin) return null;
  return {
    typeId: origin.acti_type,
    dateFrom: origin.date_from,
    dateTo: origin.date_to,
    startTime: toAbsenceTime(origin.acti_start_time),
    endTime: toAbsenceTime(origin.acti_end_time),
  };
}

/**
 * Whether changing this absence needs somebody's agreement first.
 *
 * Mirrors `ActivityAbsence.is_protected` on the server: once an approval that
 * matters has been given, an edit cannot quietly discard it and is raised as a
 * change request instead. While nobody has decided yet the absence is the
 * user's to rewrite freely.
 */
export function requiresChangeRequest(
  absence: Absence,
  type: AbsenceType | undefined,
): boolean {
  if (absence.absenceTentative || !type) return false;
  if (!type.approvableManager && !type.approvableHr) return false;
  if (!type.approvableManager && absence.approvedHr == null) return false;
  return absence.approvedManager != null;
}

/**
 * Whether booking ``newType`` over this absence simply replaces it.
 *
 * Mirrors `is_changeable_outright` on the server, which is what decides
 * between the two things a booking can do to a day somebody already has:
 * rewrite it, or leave it where it is and propose over it.
 *
 * Two rules, and the second is easy to miss. Nothing anybody decided may be
 * lost, which `requiresChangeRequest` already answers. And a day may not be
 * left empty: a booking that is a fact the moment it is written can replace
 * another outright, but one that can still be refused cannot, because a
 * refusal would take the day with it. So an absence of a type nobody
 * approves -- booked from the moment it exists, yet protected by nobody --
 * is proposed over rather than replaced whenever the new type needs a
 * decision.
 */
export function isReplacedOutright(
  absence: Absence,
  type: AbsenceType | undefined,
  newType: AbsenceType | undefined,
): boolean {
  if (absence.absenceTentative) return true;
  if (requiresChangeRequest(absence, type)) return false;
  const needsDeciding =
    !!newType && (newType.approvableManager || newType.approvableHr);
  return !(isBooked(absence) && needsDeciding);
}

/**
 * Whether this absence is booked rather than still waiting on somebody.
 *
 * Mirrors `ActivityAbsence.is_booked`: both columns hold a literal approval,
 * since a type exempt from one has it written on creation. Reading the columns
 * rather than the type's approvable flags is what keeps a proposal dashed --
 * a change request clears the column of whoever it asks, which is not always
 * the role the new type would need on its own.
 */
export function isBooked(
  absence: Pick<Absence, 'approvedManager' | 'approvedHr'>,
): boolean {
  return (
    isManagerApproved(absence.approvedManager) &&
    isHrApproved(absence.approvedHr)
  );
}

export function isManagerApproved(value: unknown) {
  return value === MANAGER_APPROVAL.APPROVED;
}

export function isHrApproved(value: unknown) {
  return value === HR_APPROVAL.APPROVED;
}

function toAbsenceTime(time: string | null) {
  if (!time) return null;
  return time.slice(0, 5); // no seconds
}

function toCalendarDate(value: Date | string): Date {
  if (value instanceof Date) {
    return value;
  }

  const isoDate = formatIsoDate(value);
  return new Date(`${isoDate}T00:00:00`);
}

export function getEventLabel(
  start: Date | string,
  end: Date | string,
  dayView = false,
) {
  const startDate = toCalendarDate(start);
  const endDate = toCalendarDate(end);

  if (!dayView) return startDate.getDate();
  if (isSameDate(startDate, endDate)) {
    return format(startDate, 'dd.MM');
  } else {
    return `${format(startDate, 'dd.MM')} – ${format(endDate, 'dd.MM')}`;
  }
}

export function formatDurationLabel(startTime: string, endTime: string) {
  const [startHours, startMinutes] = startTime.split(':').map(Number);
  const [endHours, endMinutes] = endTime.split(':').map(Number);

  let totalMinutes =
    endHours * 60 + endMinutes - (startHours * 60 + startMinutes);

  if (totalMinutes < 0) {
    totalMinutes += 24 * 60;
  }

  if (totalMinutes < 60) {
    return '';
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = String(totalMinutes % 60).padStart(2, '0');

  return `${hours}:${minutes}`;
}

export function absenceSelectionToSlot(data: SelectedAbsence): TimelineSlot {
  return {
    start: data.start,
    end: data.end,
    startTime: data.startTime,
    endTime: data.endTime,
    selectedIds: data.absenceIds,
    editedAbsenceId: data.absenceIds.length === 1 ? data.absenceIds[0] : null,
    editedAbsenceIds: data.absenceIds,
    resourceId: userResourceId(data.userId),
    userId: data.userId,
  };
}

export function groupAbsencesByUserDate(
  absences: Absence[],
): Map<string, Absence[]> {
  const grouped = new Map<string, Absence[]>();
  for (const absence of absences) {
    const key = `${absence.userId}:${absence.dateStr}`;
    const group = grouped.get(key);
    if (group) {
      group.push(absence);
    } else {
      grouped.set(key, [absence]);
    }
  }
  return grouped;
}

export function hasMixedHourlyTypes(group: Absence[]): boolean {
  if (group.length <= 1 || group[0].allDay) return false;
  const typeIds = new Set(group.map((a) => a.typeId));
  return typeIds.size > 1;
}

export function getUserAbsences(userId: number | null, absences: Absence[]) {
  if (!userId || !absences || !absences.length) {
    return [];
  }
  return absences.filter((absence) => absence.userId === userId);
}

type AbsencePayloadInput = {
  startTime?: string | null;
  endTime?: string | null;
  dateFrom?: Date | string | null;
  dateTo?: Date | string | null;
  [key: string]: unknown;
};

export function prepareAbsencePayload(values: AbsencePayloadInput) {
  const {
    dateFrom,
    dateTo,
    startTime,
    endTime,
    absenceId,
    editedAbsenceIds,
    blockRevision,
  } = values;
  return {
    // Which absence the user opened to edit, so the server can tell a reshaped
    // absence from a new one booked over the top of it.
    ...(Array.isArray(editedAbsenceIds) && editedAbsenceIds.length
      ? { edited_absence_ids: editedAbsenceIds }
      : {}),
    // The entry those days made up when the pane read them. Only sent
    // alongside something naming the days: the server resolves the entry from
    // the ids -- ``absence_id`` for an hourly row, the list for whole days --
    // and a revision with neither is a token for nothing.
    ...(typeof blockRevision === 'string' &&
    blockRevision &&
    ((Array.isArray(editedAbsenceIds) && editedAbsenceIds.length) ||
      typeof absenceId === 'number')
      ? { block_revision: blockRevision }
      : {}),
    acti_user: values.userId,
    date_from: dateFrom ? formatIsoDate(dateFrom) : null,
    date_to: dateTo ? formatIsoDate(dateTo) : null,
    ...(startTime ? { acti_start_time: startTime } : {}),
    ...(endTime ? { acti_end_time: endTime } : {}),
    ...(typeof absenceId === 'number' ? { absence_id: absenceId } : {}),
    acti_type: values.typeId,
    acti_tentative: values.tentative,
    acab_notes: values.notes,
    // Only sent by the form that is allowed to show it. Left out entirely
    // otherwise, because the server treats an absent key as "leave HR's note
    // alone" and a blank one as "clear it".
    ...(values.notesHr !== undefined ? { acab_notes_hr: values.notesHr } : {}),
  };
}

type GetAbsenceIdsForRangeOptions = {
  dayView?: boolean;
  startTime?: string | null;
  endTime?: string | null;
  excludeIds?: number[];
};

function overlapsTimedSelection(
  absence: Absence,
  startTime: string | null | undefined,
  endTime: string | null | undefined,
) {
  if (absence.allDay || !absence.startTime || !absence.endTime) {
    return true;
  }

  if (!startTime || !endTime) {
    return true;
  }

  return absence.startTime < endTime && startTime < absence.endTime;
}

export function doesTimedRangeOverlapAbsences(
  absences: Absence[],
  date: Date | string,
  startTime: string | null | undefined,
  endTime: string | null | undefined,
  excludeIds: number[] = [],
) {
  if (!startTime || !endTime) {
    return false;
  }

  const day = formatIsoDate(date);

  return absences.some(
    (absence) =>
      absence.dateStr === day &&
      !excludeIds.includes(absence.id) &&
      overlapsTimedSelection(absence, startTime, endTime),
  );
}

export function getAbsencesIdsForRange(
  dateFrom: Date | string,
  dateTo: Date | string,
  absences: Absence[],
  options: GetAbsenceIdsForRangeOptions = {},
): number[] {
  const {
    dayView = false,
    startTime = null,
    endTime = null,
    excludeIds = [],
  } = options;

  if (dayView) {
    const day = formatIsoDate(dateFrom);
    return absences
      .filter(
        (absence) =>
          absence.dateStr === day &&
          !excludeIds.includes(absence.id) &&
          overlapsTimedSelection(absence, startTime, endTime),
      )
      .map((absence) => absence.id);
  }

  const range = daysRange(toCalendarDate(dateFrom), toCalendarDate(dateTo)).map(
    (d) => formatIsoDate(d),
  );
  return absences
    .filter((absence) => !excludeIds.includes(absence.id))
    .filter((absence) => range.includes(absence.dateStr))
    .map((absence) => absence.id);
}

export function getEventTimeDelta(info: { milliseconds: number }) {
  if (info.milliseconds !== 0) {
    return info.milliseconds / 1000 / 60;
  }
  return 0;
}

export function shiftTime(time: string, deltaMinutes: number) {
  const [h, m] = time.split(':').map(Number);
  const d = new Date(1970, 0, 1, h, m, 0, 0);
  d.setMinutes(d.getMinutes() + deltaMinutes);
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

export function selectAbsenceTypes(data: Array<AbsenceTypeDto>) {
  return data.map(absenceTypeFromDto);
}
function absenceTypeFromDto(absenceTypeDto: AbsenceTypeDto): AbsenceType {
  return {
    id: absenceTypeDto.id,
    name: absenceTypeDto.acty_name,
    groupId: absenceTypeDto.acty_group,
    tentativeable: absenceTypeDto.acty_tentativeable,
    editableUser: absenceTypeDto.acty_editable_user,
    editableManager: absenceTypeDto.acty_editable_manager,
    editableHr: absenceTypeDto.acty_editable_hr,
    approvableManager: absenceTypeDto.acty_approvable_manager,
    approvableHr: absenceTypeDto.acty_approvable_hr,
    backdateable: absenceTypeDto.acty_backdateable,
  };
}

export function selectActivityTypeGroups(
  data: Array<ActivityTypeGroupDto>,
): Array<ActivityTypeGroup> {
  return data.map(activityTypeGroupFromDto);
}

function activityTypeGroupFromDto(
  dto: ActivityTypeGroupDto,
): ActivityTypeGroup {
  return {
    id: dto.id,
    name: dto.actg_name,
    colourFg: dto.actg_colour_fg,
    colourBg: dto.actg_colour_bg,
  };
}
